import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { LogOut } from "lucide-react";
import { requireAuth } from "@/lib/auth/require-auth";
import { useAuthActions, useOrgStore } from "@/stores";
import Button from "@/components/ui/button/Button";

// Org-setup group (pathless): auth required, org NOT required. Used for
// `/select-org` after login before an org is picked. If user already has an
// active org, bounce to `/`.
export const Route = createFileRoute("/_org-setup")({
  beforeLoad: async ({ location }) => {
    await requireAuth(location.href);
    // If a current org IS already known, bounce — except when the user is
    // here intentionally via Switch Org (force=1). Without this escape
    // hatch, single-org users could never reach /select-org because the
    // root beforeLoad auto-hydrates and re-selects their only org before
    // this guard ever sees the cleared state.
    const force = new URLSearchParams(location.searchStr).get("force");
    if (force === "1") return;
    if (useOrgStore.getState().currentOrg) {
      throw redirect({ to: "/" });
    }
  },
  component: OrgSetupLayout,
});

// Top-right escape hatch for users who land on /select-org with the wrong
// account (e.g. signed in as a personal account but needed to use a corp one).
// Without this, the only way out is browser dev-tools or clearing cookies,
// since the layout has no header/UserDropdown.
function LogoutCorner() {
  const t = useTranslations("system-admin.common");
  const navigate = useNavigate();
  const { logout } = useAuthActions();

  const handleLogout = async () => {
    await logout();
    navigate({ to: "/signin" });
  };

  return (
    <div className="absolute right-4 top-4">
      <Button variant="outline" size="sm" onClick={handleLogout}>
        <LogOut className="size-4 mr-2" aria-hidden />
        {t("landing.actions.logout")}
      </Button>
    </div>
  );
}

function OrgSetupLayout() {
  return (
    <div className="relative flex min-h-dvh items-start justify-center overflow-y-auto bg-[var(--background)] p-4 pt-20 sm:items-center sm:pt-4">
      <LogoutCorner />
      <Outlet />
    </div>
  );
}
